import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronRight, ArrowLeft, Layers, Building2, FileText, ShieldAlert,
  DollarSign, TrendingUp, Users, AlertTriangle, Search
} from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import EmptyState from '@/components/EmptyState';

const COLORS = {
  navy: '#0D1B2A',
  darkNavy: '#0B1E3D',
  saffron: '#FF9933',
  teal: '#00B4D8',
  green: '#1D9E75',
  red: '#E24B4A',
  amber: '#E8A020',
  slate: '#5C6B7A',
  bg: '#F5F7FA',
  blue: '#1A4C7C',
};

function fmt$(val) {
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val?.toLocaleString() || 0}`;
}

// ─── Breadcrumb ───
function Breadcrumb({ trail, onNavigate }) {
  return (
    <div className="flex items-center gap-1.5 text-sm flex-wrap" data-testid="drilldown-breadcrumb">
      {trail.map((crumb, i) => (
        <React.Fragment key={crumb.label}>
          {i > 0 && <ChevronRight className="w-3.5 h-3.5" style={{ color: COLORS.slate }} />}
          {i < trail.length - 1 ? (
            <button
              onClick={() => onNavigate(i)}
              className="hover:underline transition-colors font-medium"
              style={{ color: COLORS.blue }}
              data-testid={`breadcrumb-${i}`}
            >
              {crumb.label}
            </button>
          ) : (
            <span className="font-semibold" style={{ color: COLORS.navy }}>{crumb.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Level 1: Classes Overview ───
function ClassesView({ onSelectClass }) {
  const api = useApi();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/drilldown/classes').then(r => setClasses(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <LoadingSkeleton count={8} />;

  const totalGWP = classes.reduce((s, c) => s + c.gwp, 0);
  const totalPolicies = classes.reduce((s, c) => s + c.policy_count, 0);

  return (
    <div className="space-y-5" data-testid="classes-view">
      {/* Summary Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKPI label="Total Classes" value={classes.length} color={COLORS.blue} />
        <MiniKPI label="Active Policies" value={totalPolicies.toLocaleString()} color={COLORS.green} />
        <MiniKPI label="Total GWP" value={fmt$(totalGWP)} color="#1A4C7C" />
        <MiniKPI label="Avg Premium" value={fmt$(totalGWP / (totalPolicies || 1))} color={COLORS.teal} />
      </div>

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {classes.map(cls => (
          <button
            key={cls.class_name}
            onClick={() => onSelectClass(cls)}
            data-testid={`class-card-${cls.class_name.replace(/\s/g, '-').toLowerCase()}`}
            className="text-left group"
          >
            <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white hover:border-[#1A4C7C] hover:shadow-md transition-all duration-200 h-full">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: COLORS.blue + '12' }}>
                    <Layers className="w-4.5 h-4.5" style={{ color: COLORS.blue }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: COLORS.navy }}>{cls.class_name}</h3>
                    <p className="text-[11px]" style={{ color: COLORS.slate }}>{cls.policy_count} policies</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: COLORS.blue }} />
              </div>
              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-[rgba(136,150,168,0.12)]">
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>GWP</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: COLORS.navy }}>{fmt$(cls.gwp)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Avg Prem</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: cls.band_status && cls.band_status !== 'within' ? '#B27516' : COLORS.navy }} data-testid={`class-avg-premium-${cls.class_name.replace(/\s/g, '-').toLowerCase()}`}>{fmt$(cls.avg_premium)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Avg Score</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: COLORS.navy }}>{cls.avg_score}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Claims</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: COLORS.slate || '#5C6B7A' }}>{cls.claims_count}</p>
                </div>
              </div>
              {(cls.expected_band || cls.low_credibility || (cls.band_status && cls.band_status !== 'within')) && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {cls.expected_band && (
                    <span className="text-[10px]" style={{ color: COLORS.slate }}>
                      Typical {fmt$(cls.expected_band.typical_min)}–{fmt$(cls.expected_band.typical_max)}
                    </span>
                  )}
                  {cls.band_status && cls.band_status !== 'within' && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider" style={{ background: 'rgba(232,160,32,0.14)', color: '#B27516' }} data-testid={`class-outlier-${cls.class_name.replace(/\s/g, '-').toLowerCase()}`}>
                      AVG {cls.band_status.toUpperCase()} BAND
                    </span>
                  )}
                  {cls.low_credibility && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider" style={{ background: 'rgba(136,150,168,0.14)', color: '#5C6B7A' }} data-testid={`class-lowsample-${cls.class_name.replace(/\s/g, '-').toLowerCase()}`}>
                      LOW SAMPLE ({cls.policy_count})
                    </span>
                  )}
                </div>
              )}
              {/* GWP share bar */}
              <div className="mt-3">
                <div className="w-full h-1.5 rounded-full" style={{ background: COLORS.bg }}>
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((cls.gwp / (totalGWP || 1)) * 100, 100)}%`, background: COLORS.blue }}
                  />
                </div>
                <p className="text-[10px] mt-1 text-right" style={{ color: COLORS.slate }}>
                  {((cls.gwp / (totalGWP || 1)) * 100).toFixed(1)}% of GWP
                </p>
              </div>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Level 2: Clients within a Class ───
function ClientsView({ className, onSelectClient }) {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get(`/drilldown/class/${encodeURIComponent(className)}/clients`)
      .then(r => setClients(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api, className]);

  if (loading) return <LoadingSkeleton count={6} />;

  const filtered = search
    ? clients.filter(c => c.business_name.toLowerCase().includes(search.toLowerCase()))
    : clients;

  const totalGWP = clients.reduce((s, c) => s + c.gwp, 0);

  return (
    <div className="space-y-4" data-testid="clients-view">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKPI label="Clients" value={clients.length} color={COLORS.blue} />
        <MiniKPI label="Total Policies" value={clients.reduce((s, c) => s + c.policy_count, 0)} color={COLORS.green} />
        <MiniKPI label="Class GWP" value={fmt$(totalGWP)} color="#1A4C7C" />
        <MiniKPI label="Avg Score" value={`${Math.round(clients.reduce((s, c) => s + c.avg_score, 0) / (clients.length || 1)).toLocaleString()}/10K`} color={COLORS.teal} />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.slate }} />
        <input
          type="text"
          placeholder="Search clients..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 text-sm rounded-sm border border-[rgba(136,150,168,0.25)] focus:outline-none focus:border-[#1A4C7C] bg-white"
          style={{ color: COLORS.navy }}
          data-testid="client-search-input"
        />
      </div>

      {/* Clients Table */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="border-b border-[rgba(136,150,168,0.15)]" style={{ background: COLORS.bg }}>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Business</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Industry</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Broker</th>
                <th className="text-center py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Policies</th>
                <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>GWP</th>
                <th className="text-center py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Score</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-0"><EmptyState type="clients" compact /></td></tr>
              ) : filtered.map(client => (
                <tr
                  key={client.business_name}
                  onClick={() => onSelectClient(client)}
                  className="border-b border-[rgba(136,150,168,0.1)] hover:bg-[#F5F7FA] cursor-pointer transition-colors group"
                  data-testid={`client-row-${client.entity_id}`}
                >
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 shrink-0" style={{ color: COLORS.blue }} />
                      <span className="font-medium truncate max-w-[200px]" style={{ color: COLORS.navy }}>{client.business_name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4 text-xs truncate max-w-[150px]" style={{ color: COLORS.slate }}>{client.industry || '—'}</td>
                  <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{client.broker_name || '—'}</td>
                  <td className="py-2.5 px-4 text-center text-xs font-medium" style={{ color: COLORS.navy }}>{client.policy_count}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: COLORS.navy }}>{fmt$(client.gwp)}</td>
                  <td className="py-2.5 px-4 text-center">
                    <ScoreBadge score={client.avg_score} />
                  </td>
                  <td className="py-1 px-2">
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: COLORS.blue }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Level 3: Policies for a Client ───
function PoliciesView({ businessName, className, onSelectPolicy }) {
  const api = useApi();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = className ? `?class_name=${encodeURIComponent(className)}` : '';
    api.get(`/drilldown/client/${encodeURIComponent(businessName)}/policies${params}`)
      .then(r => setPolicies(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api, businessName, className]);

  if (loading) return <LoadingSkeleton count={4} />;

  const totalPremium = policies.reduce((s, p) => s + (p.annual_premium || 0), 0);
  const totalClaims = policies.reduce((s, p) => s + (p.claims_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="policies-view">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKPI label="Policies" value={policies.length} color={COLORS.blue} />
        <MiniKPI label="Total Premium" value={fmt$(totalPremium)} color="#1A4C7C" />
        <MiniKPI label="Open Claims" value={totalClaims} color={totalClaims > 0 ? COLORS.red : COLORS.green} />
        <MiniKPI label="Avg Score" value={`${Math.round(policies.reduce((s, p) => s + (p.composite_score || 0), 0) / (policies.length || 1)).toLocaleString()}/10K`} color={COLORS.teal} />
      </div>

      {/* Policies Table */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="client-policies-table">
            <thead>
              <tr className="border-b border-[rgba(136,150,168,0.15)]" style={{ background: COLORS.bg }}>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Policy #</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Class</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Inception</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Expiry</th>
                <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Premium</th>
                <th className="text-center py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Claims</th>
                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Status</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {policies.length === 0 ? (
                <tr><td colSpan={8} className="p-0"><EmptyState type="policies" compact /></td></tr>
              ) : policies.map(p => {
                const statusColor = p.status === 'active' ? COLORS.green : p.status === 'cancelled' ? COLORS.red : COLORS.slate;
                return (
                  <tr
                    key={p.policy_id}
                    onClick={() => onSelectPolicy(p)}
                    className="border-b border-[rgba(136,150,168,0.1)] hover:bg-[#F5F7FA] cursor-pointer transition-colors group"
                    data-testid={`policy-row-${p.policy_id}`}
                  >
                    <td className="py-2.5 px-4 font-mono text-xs font-medium" style={{ color: COLORS.blue }}>{p.policy_number}</td>
                    <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{p.class}</td>
                    <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{new Date(p.inception_date).toLocaleDateString('en-AU')}</td>
                    <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{new Date(p.expiry_date).toLocaleDateString('en-AU')}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: COLORS.navy }}>${p.annual_premium?.toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-center">
                      {p.claims_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium" style={{ background: COLORS.red + '15', color: COLORS.red }}>
                          <ShieldAlert className="w-3 h-3" /> {p.claims_count}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: COLORS.green }}>0</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <span className="px-2 py-0.5 rounded-sm text-[11px] font-medium capitalize" style={{ background: statusColor + '15', color: statusColor }}>{p.status}</span>
                    </td>
                    <td className="py-1 px-2">
                      <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: COLORS.blue }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Level 4: Policy Detail ───
function PolicyDetailView({ policyId }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get(`/drilldown/policy/${policyId}/detail`)
      .then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api, policyId]);

  if (loading) return <LoadingSkeleton count={4} />;
  if (!data) return <p style={{ color: COLORS.slate }}>Policy not found</p>;

  const { policy, claims, endorsements, cancellation, entity, summary } = data;
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'claims', label: `Claims (${summary.claims_count})` },
    { id: 'endorsements', label: `Endorsements (${summary.endorsements_count})` },
    { id: 'entity', label: 'Entity' },
  ];

  return (
    <div className="space-y-4" data-testid="policy-detail-view">
      {/* Policy Header */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold font-mono" style={{ color: COLORS.navy }}>{policy.policy_number}</h2>
              <span className="px-2 py-0.5 rounded-sm text-[11px] font-semibold capitalize"
                style={{
                  background: (policy.status === 'active' ? COLORS.green : policy.status === 'cancelled' ? COLORS.red : COLORS.slate) + '15',
                  color: policy.status === 'active' ? COLORS.green : policy.status === 'cancelled' ? COLORS.red : COLORS.slate,
                }}>
                {policy.status}
              </span>
              {cancellation && (
                <span className="px-2 py-0.5 rounded-sm text-[11px] font-semibold" style={{ background: COLORS.red + '15', color: COLORS.red }}>CANCELLED</span>
              )}
            </div>
            <p className="text-sm" style={{ color: COLORS.slate }}>{policy.business_name} — {policy.class}</p>
          </div>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Premium</p>
              <p className="text-lg font-semibold font-mono" style={{ color: COLORS.navy }}>${policy.annual_premium?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Sum Insured</p>
              <p className="text-lg font-semibold font-mono" style={{ color: COLORS.navy }}>{fmt$(policy.sum_insured || 0)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.slate }}>Score</p>
              <p className="text-lg font-semibold" style={{ color: COLORS.navy }}>{policy.composite_score}<span className="text-xs" style={{ color: COLORS.slate }}>/10K</span></p>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniKPI label="Inception" value={new Date(policy.inception_date).toLocaleDateString('en-AU')} color={COLORS.blue} />
        <MiniKPI label="Expiry" value={new Date(policy.expiry_date).toLocaleDateString('en-AU')} color={COLORS.blue} />
        <MiniKPI label="Claims Reserve" value={fmt$(summary.total_reserve)} color={summary.total_reserve > 0 ? COLORS.red : COLORS.green} />
        <MiniKPI label="Claims Paid" value={fmt$(summary.total_paid)} color="#1A4C7C" />
        <MiniKPI label="Premium Adj." value={`${summary.premium_adjustments >= 0 ? '+' : ''}$${summary.premium_adjustments.toLocaleString()}`} color={COLORS.teal} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(136,150,168,0.2)]">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-[#1A4C7C]' : 'border-transparent hover:border-[rgba(136,150,168,0.3)]'}`}
            style={{ color: tab === t.id ? COLORS.navy : COLORS.slate }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <DetailRow label="Policy Number" value={policy.policy_number} />
              <DetailRow label="Class" value={policy.class} />
              <DetailRow label="Business" value={policy.business_name} />
              <DetailRow label="ABN" value={<RegistryLink abn={policy.abn} />} />
              <DetailRow label="Industry" value={policy.industry || '—'} />
              <DetailRow label="Broker" value={policy.broker_name || '—'} />
            </div>
            <div className="space-y-3">
              <DetailRow label="Decision" value={policy.decision} badge />
              <DetailRow label="Inception" value={new Date(policy.inception_date).toLocaleDateString('en-AU')} />
              <DetailRow label="Expiry" value={new Date(policy.expiry_date).toLocaleDateString('en-AU')} />
              <DetailRow label="Annual Premium" value={`$${policy.annual_premium?.toLocaleString()}`} />
              <DetailRow label="Sum Insured" value={fmt$(policy.sum_insured || 0)} />
              <DetailRow label="Created" value={new Date(policy.created_at).toLocaleString()} />
            </div>
          </div>
        </Card>
      )}

      {tab === 'claims' && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          {claims.length === 0 ? (
            <EmptyState type="claims" compact />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="policy-claims-table">
                <thead>
                  <tr className="border-b border-[rgba(136,150,168,0.15)]" style={{ background: COLORS.bg }}>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Claim Ref</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Type</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Description</th>
                    <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Reserve</th>
                    <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Paid</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Status</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map(cl => (
                    <tr key={cl.claim_id} className="border-b border-[rgba(136,150,168,0.1)]">
                      <td className="py-2.5 px-4 font-mono text-xs" style={{ color: COLORS.blue }}>{cl.claim_reference}</td>
                      <td className="py-2.5 px-4 text-xs capitalize" style={{ color: COLORS.slate }}>{cl.claim_type}</td>
                      <td className="py-2.5 px-4 text-xs max-w-[200px] truncate" style={{ color: COLORS.navy }}>{cl.description}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: COLORS.navy }}>${(cl.reserve_amount || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: COLORS.navy }}>${(cl.paid_amount || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-4">
                        <ClaimStatusBadge status={cl.status} />
                      </td>
                      <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{new Date(cl.created_at).toLocaleDateString('en-AU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'endorsements' && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          {endorsements.length === 0 ? (
            <div className="p-8 text-center" style={{ color: COLORS.slate }}>
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No endorsements on this policy</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="policy-endorsements-table">
                <thead>
                  <tr className="border-b border-[rgba(136,150,168,0.15)]" style={{ background: COLORS.bg }}>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>ID</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Type</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Description</th>
                    <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Premium Adj.</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Effective</th>
                    <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {endorsements.map(e => (
                    <tr key={e.endorsement_id} className="border-b border-[rgba(136,150,168,0.1)]">
                      <td className="py-2.5 px-4 font-mono text-xs" style={{ color: COLORS.blue }}>{e.endorsement_id?.slice(0, 8)}</td>
                      <td className="py-2.5 px-4 text-xs capitalize" style={{ color: COLORS.navy }}>{e.type}</td>
                      <td className="py-2.5 px-4 text-xs max-w-[200px] truncate" style={{ color: COLORS.slate }}>{e.description || '—'}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs" style={{ color: e.premium_adjustment > 0 ? COLORS.green : e.premium_adjustment < 0 ? COLORS.red : COLORS.slate }}>
                        {e.premium_adjustment > 0 ? '+' : ''}{e.premium_adjustment ? `$${e.premium_adjustment.toLocaleString()}` : '—'}
                      </td>
                      <td className="py-2.5 px-4 text-xs" style={{ color: COLORS.slate }}>{e.effective_date ? new Date(e.effective_date).toLocaleDateString('en-AU') : '—'}</td>
                      <td className="py-2.5 px-4">
                        <span className="px-2 py-0.5 rounded-sm text-[11px] font-medium capitalize" style={{ background: COLORS.green + '15', color: COLORS.green }}>{e.status || 'applied'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'entity' && (
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          {entity ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailRow label="Legal Name" value={entity.legal_name} />
              <DetailRow label="ABN/NZBN" value={<RegistryLink abn={entity.abn_nzbn || entity.abn} />} />
              <DetailRow label="Industry" value={entity.industry_code || entity.industry || '—'} />
              <DetailRow label="Location" value={`${entity.city || ''}, ${entity.state || ''} ${entity.postcode || ''}`} />
              <DetailRow label="Employee Count" value={entity.employee_count || '—'} />
              <DetailRow label="Annual Revenue" value={entity.annual_revenue ? fmt$(entity.annual_revenue) : '—'} />
              <DetailRow label="Entity Type" value={entity.entity_type || '—'} />
              <DetailRow label="Risk Tier" value={entity.risk_tier || '—'} />
            </div>
          ) : (
            <p className="text-sm text-center py-4" style={{ color: COLORS.slate }}>Entity data not available</p>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Shared Components ───
function MiniKPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: COLORS.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || COLORS.navy }}>{value}</p>
    </Card>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 7000 ? COLORS.red : score >= 4000 ? COLORS.amber : COLORS.green;
  return (
    <span className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-mono font-semibold" style={{ background: color + '15', color }}>
      {score}
    </span>
  );
}

function ClaimStatusBadge({ status }) {
  const map = {
    fnol_received: { bg: COLORS.teal, label: 'FNOL' },
    under_review: { bg: COLORS.amber, label: 'Review' },
    settled: { bg: COLORS.green, label: 'Settled' },
    closed: { bg: COLORS.slate, label: 'Closed' },
    denied: { bg: COLORS.red, label: 'Denied' },
  };
  const cfg = map[status] || { bg: COLORS.slate, label: status };
  return (
    <span className="px-2 py-0.5 rounded-sm text-[11px] font-medium" style={{ background: cfg.bg + '15', color: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function DetailRow({ label, value, badge }) {
  const decisionColors = {
    APPROVE: COLORS.green,
    REFER: COLORS.amber,
    DECLINE: COLORS.red,
    CONDITIONS: COLORS.teal,
  };
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[rgba(136,150,168,0.08)]">
      <span className="text-xs uppercase tracking-wider" style={{ color: COLORS.slate }}>{label}</span>
      {badge ? (
        <span className="px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ background: (decisionColors[value] || COLORS.slate) + '15', color: decisionColors[value] || COLORS.slate }}>
          {value}
        </span>
      ) : (
        <span className="text-sm font-medium" style={{ color: COLORS.navy }}>{value}</span>
      )}
    </div>
  );
}

function LoadingSkeleton({ count = 4 }) {
  return (
    <div className="space-y-3">
      {[...Array(count)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}
    </div>
  );
}

// ─── Main Drill-Down Page ───
export default function PortfolioDrilldownPage() {
  // Navigation state: [{ level, label, data }]
  const [trail, setTrail] = useState([{ level: 'classes', label: 'Portfolio', data: {} }]);

  const currentLevel = trail[trail.length - 1];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navigateTo = useCallback((index) => {
    setTrail(prev => prev.slice(0, index + 1));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectClass = useCallback((cls) => {
    setTrail(prev => [...prev, { level: 'clients', label: cls.class_name, data: cls }]);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectClient = useCallback((client) => {
    const className = trail.find(t => t.level === 'clients')?.data?.class_name;
    setTrail(prev => [...prev, { level: 'policies', label: client.business_name, data: { ...client, className } }]);
  }, [trail]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectPolicy = useCallback((policy) => {
    setTrail(prev => [...prev, { level: 'detail', label: policy.policy_number, data: policy }]);
  }, []);

  return (
    <div className="space-y-5" data-testid="portfolio-drilldown-page">
      <div className="flex items-center justify-between">
        <div>
          <Breadcrumb trail={trail} onNavigate={navigateTo} />
        </div>
        {trail.length > 1 && (
          <button
            onClick={() => navigateTo(trail.length - 2)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium transition-colors hover:bg-[rgba(26,76,124,0.08)]"
            style={{ color: COLORS.blue }}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}
      </div>

      {currentLevel.level === 'classes' && <ClassesView onSelectClass={selectClass} />}
      {currentLevel.level === 'clients' && (
        <ClientsView className={currentLevel.data.class_name} onSelectClient={selectClient} />
      )}
      {currentLevel.level === 'policies' && (
        <PoliciesView
          businessName={currentLevel.data.business_name}
          className={currentLevel.data.className}
          onSelectPolicy={selectPolicy}
        />
      )}
      {currentLevel.level === 'detail' && <PolicyDetailView policyId={currentLevel.data.policy_id} />}
    </div>
  );
}
